import React, { useState, useEffect } from "react";
import {
  AiOutlineWallet,
  AiOutlineHistory,
  AiOutlineEdit,
  AiOutlineSearch,
} from "react-icons/ai";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Input,
  Badge,
} from "reactstrap";
import "../../../styles/dashboard/SuperAdmin/WalletCreditManagement.css";
import { fetchCreditCosts, updateCreditCosts } from "../../services/api";

// Simulate user role (in real app, get from auth context)
const USER_ROLE = "Super Admin"; // Change to "Admin" to test view-only mode

export default function WalletCreditManagement({ role = "superadmin" }) {
  // ────── DUMMY DATA: Organizations with Wallets ──────
  const [organizations, setOrganizations] = useState([
    {
      id: 1,
      name: "TechVision Solutions",
      email: "admin@techvision.com",
      walletBalance: 4850,
      lastTransaction: "2025-10-28",
      status: "Active",
    },
    {
      id: 2,
      name: "GreenLeaf Foundation",
      email: "info@greenleaf.org",
      walletBalance: 1200,
      lastTransaction: "2025-10-25",
      status: "Active",
    },
    {
      id: 3,
      name: "Alpha Education Trust",
      email: "contact@alphaedu.org",
      walletBalance: 0,
      lastTransaction: "2025-09-15",
      status: "Deactivate",
    },
  ]);

  // ────── CREDIT COSTS (editable by Super Admin only) ──────
  const [creditCosts, setCreditCosts] = useState({
    basicCreditCost: 10,
    advancedCreditCost: 25,
    comboCreditCost: 35,
    panVerificationCost: 5,
    courtCaseCheckCost: 10,
    panToBankAccountCost: 10,
    mobileToBankAccountCost: 10,
    mobilePrefillCost: 10,
    panToBankAccountProvider: "idspay",
    mobileToBankAccountProvider: "idspay",
  });

  const [formData, setFormData] = useState({ ...creditCosts });
  const [loadingCosts, setLoadingCosts] = useState(false);

  // ────── MODALS ──────
  const [transactionModal, setTransactionModal] = useState(false);
  const [editCostModal, setEditCostModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);

  // ────── TOAST ──────
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  // ────── SEARCH ──────
  const [searchTerm, setSearchTerm] = useState("");
  const filteredOrgs = organizations.filter(
    (org) =>
      org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ────── TOAST AUTO-HIDE ──────
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast({ show: false }), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // ────── LOAD CREDIT COSTS FROM API (Super Admin) ──────
  useEffect(() => {
    const isSuperAdmin = USER_ROLE === "Super Admin";
    if (!isSuperAdmin) return;

    const loadCosts = async () => {
      setLoadingCosts(true);
      const res = await fetchCreditCosts();
      if (res.success && res.data) {
        const payload = Array.isArray(res.data) && res.data.length > 0 ? res.data[0] : res.data;
        const next = {
          basicCreditCost: Number(payload.basicCreditCost ?? 10),
          advancedCreditCost: Number(payload.advancedCreditCost ?? 25),
          comboCreditCost: Number(payload.comboCreditCost ?? 35),
          panVerificationCost: Number(payload.panVerificationCost ?? 5),
          courtCaseCheckCost: Number(payload.courtCaseCheckCost ?? 10),
          panToBankAccountCost: Number(payload.panToBankAccountCost ?? 10),
          mobileToBankAccountCost: Number(payload.mobileToBankAccountCost ?? 10),
          mobilePrefillCost: Number(payload.mobilePrefillCost ?? 10),
          panToBankAccountProvider: String(payload.panToBankAccountProvider ?? "idspay"),
          mobileToBankAccountProvider: String(payload.mobileToBankAccountProvider ?? "idspay"),
        };
        setCreditCosts(next);
        setFormData(next);
      } else if (!res.success) {
        showToast(res.error || "Failed to load credit costs", "error");
      }
      setLoadingCosts(false);
    };

    loadCosts();
  }, []);

  const showToast = (msg, type = "success") =>
    setToast({ show: true, message: msg, type });

  // ────── MODAL HANDLERS ──────
  const openTransactionModal = (org) => {
    setSelectedOrg(org);
    setTransactionModal(true);
  };

  const openEditCostModal = () => {
    setFormData({ ...creditCosts });
    setEditCostModal(true);
  };

  const closeModals = () => {
    setTransactionModal(false);
    setEditCostModal(false);
    setSelectedOrg(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveCosts = async () => {
    const isSuperAdmin = USER_ROLE === "Super Admin";
    if (!isSuperAdmin) return;

    const payload = {
      basicCreditCost: Number(formData.basicCreditCost),
      advancedCreditCost: Number(formData.advancedCreditCost),
      comboCreditCost: Number(formData.comboCreditCost),
      panVerificationCost: Number(formData.panVerificationCost),
      courtCaseCheckCost: Number(formData.courtCaseCheckCost),
      panToBankAccountCost: Number(formData.panToBankAccountCost),
      mobileToBankAccountCost: Number(formData.mobileToBankAccountCost),
      mobilePrefillCost: Number(formData.mobilePrefillCost),
      panToBankAccountProvider: String(formData.panToBankAccountProvider || "idspay"),
      mobileToBankAccountProvider: String(formData.mobileToBankAccountProvider || "idspay"),
    };

    const res = await updateCreditCosts(payload);
    if (!res.success) {
      showToast(res.error || "Failed to save credit costs", "error");
      return;
    }

    const saved = res.data || payload;
    const next = {
      basicCreditCost: Number(saved.basicCreditCost ?? payload.basicCreditCost),
      advancedCreditCost: Number(saved.advancedCreditCost ?? payload.advancedCreditCost),
      comboCreditCost: Number(saved.comboCreditCost ?? payload.comboCreditCost),
      panVerificationCost: Number(saved.panVerificationCost ?? payload.panVerificationCost),
      courtCaseCheckCost: Number(saved.courtCaseCheckCost ?? payload.courtCaseCheckCost),
      panToBankAccountCost: Number(saved.panToBankAccountCost ?? payload.panToBankAccountCost),
      mobileToBankAccountCost: Number(saved.mobileToBankAccountCost ?? payload.mobileToBankAccountCost),
      mobilePrefillCost: Number(saved.mobilePrefillCost ?? payload.mobilePrefillCost),
      panToBankAccountProvider: String(saved.panToBankAccountProvider ?? payload.panToBankAccountProvider),
      mobileToBankAccountProvider: String(saved.mobileToBankAccountProvider ?? payload.mobileToBankAccountProvider),
    };

    setCreditCosts(next);
    setFormData(next);
    showToast("Credit costs updated successfully!", "success");
    closeModals();
  };

  // ────── DUMMY TRANSACTION HISTORY ──────
  const transactionHistory = [
    { date: "2025-10-28", type: "Credit Purchase", amount: 5000, status: "Success" },
    { date: "2025-10-20", type: "Basic Plan Used", amount: -150, status: "Deducted" },
    { date: "2025-10-15", type: "Advanced Plan", amount: -375, status: "Deducted" },
    { date: "2025-09-30", type: "Credit Purchase", amount: 2000, status: "Success" },
  ];

  const isSuperAdmin = USER_ROLE === "Super Admin";

  return (
    <div className="wc-container">
      {/* Toast */}
      {toast.show && (
        <div className={`wc-toast wc-toast-${toast.type}`}>
          <span>{toast.message}</span>
          <div className="wc-toast-progress" />
        </div>
      )}

      <Row>
        {/* ────── CREDIT COST CARD (Top) ────── */}
        <Col lg="4" md="6" className="mb-4">
          <Card className="wc-cost-card h-100 shadow-sm border-0">
            <CardBody className="d-flex flex-column">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <CardTitle tag="h5" className="mb-0 wc-cost-title">
                  Credit Cost Settings
                </CardTitle>
                {isSuperAdmin && (
                  <Button
                    size="sm"
                    color="primary"
                    className="wc-btn-edit-cost"
                    onClick={openEditCostModal}
                  >
                    <AiOutlineEdit size={14} /> Edit
                  </Button>
                )}
              </div>
              <div className="wc-cost-item">
                <span className="wc-cost-label">Basic Credit</span>
                <strong className="wc-cost-value">₹{creditCosts.basicCreditCost}</strong>
              </div>
              <div className="wc-cost-item">
                <span className="wc-cost-label">Advanced Credit</span>
                <strong className="wc-cost-value">₹{creditCosts.advancedCreditCost}</strong>
              </div>
              <div className="wc-cost-item">
                <span className="wc-cost-label">Combo Credit</span>
                <strong className="wc-cost-value">₹{creditCosts.comboCreditCost}</strong>
              </div>
              <div className="wc-cost-item">
                <span className="wc-cost-label">PAN Verification</span>
                <strong className="wc-cost-value">₹{creditCosts.panVerificationCost}</strong>
              </div>
              <div className="wc-cost-item">
                <span className="wc-cost-label">Court Case Check</span>
                <strong className="wc-cost-value">₹{creditCosts.courtCaseCheckCost}</strong>
              </div>
              <div className="wc-cost-item">
                <span className="wc-cost-label">PAN To Bank Account</span>
                <strong className="wc-cost-value">₹{creditCosts.panToBankAccountCost}</strong>
              </div>
              <div className="wc-cost-item">
                <span className="wc-cost-label">Mobile To Bank Account</span>
                <strong className="wc-cost-value">₹{creditCosts.mobileToBankAccountCost}</strong>
              </div>
              <div className="wc-cost-item">
                <span className="wc-cost-label">Mobile Prefill</span>
                <strong className="wc-cost-value">₹{creditCosts.mobilePrefillCost}</strong>
              </div>
              <div className="wc-cost-item">
                <span className="wc-cost-label">PAN To Bank Provider</span>
                <strong className="wc-cost-value text-uppercase">{creditCosts.panToBankAccountProvider}</strong>
              </div>
              <div className="wc-cost-item">
                <span className="wc-cost-label">Mobile To Bank Provider</span>
                <strong className="wc-cost-value text-uppercase">{creditCosts.mobileToBankAccountProvider}</strong>
              </div>
              {!isSuperAdmin && (
                <small className="text-muted mt-2">Only Super Admin can edit costs</small>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* ────── WALLET TABLE CARD ────── */}
        <Col lg="8" md="12">
          <Card className="wc-table-card shadow-sm border-0">
            <CardBody className="p-4">
              <Row className="align-items-center mb-4">
                <Col>
                  <CardTitle tag="h4" className="wc-title mb-0">
                    Organization Wallets
                  </CardTitle>
                </Col>
              </Row>

              {/* Search */}
              <Row className="mb-4">
                <Col md="6">
                  <div className="wc-search-wrapper position-relative">
                    <AiOutlineSearch className="wc-search-icon" />
                    <Input
                      type="text"
                      placeholder="Search by name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="wc-search-input ps-5"
                    />
                  </div>
                </Col>
              </Row>

              {/* Table */}
              <div className="wc-table-wrapper">
                <table responsive hover className="wc-table align-middle">
                  <thead className="wc-thead">
                    <tr>
                      <th className="wc-th">Organization</th>
                      <th className="wc-th d-none d-md-table-cell">Email</th>
                      <th className="wc-th text-center">Wallet Balance</th>
                      <th className="wc-th d-none d-lg-table-cell">Last Transaction</th>
                      <th className="wc-th">Status</th>
                      <th className="wc-th text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrgs.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4 text-muted">
                          No organizations found
                        </td>
                      </tr>
                    ) : (
                      filteredOrgs.map((org) => (
                        <tr key={org.id} className="wc-tr">
                          <td className="wc-td fw-semibold">{org.name}</td>
                          <td className="wc-td d-none d-md-table-cell">
                            <a href={`mailto:${org.email}`} className="text-primary text-decoration-none">
                              {org.email}
                            </a>
                          </td>
                          <td className="wc-td text-center">
                            <Badge
                              color={org.walletBalance > 0 ? "success" : "danger"}
                              className="wc-balance-badge"
                            >
                              <AiOutlineWallet size={14} /> ₹{org.walletBalance}
                            </Badge>
                          </td>
                          <td className="wc-td d-none d-lg-table-cell">
                            {new Date(org.lastTransaction).toLocaleDateString()}
                          </td>
                          <td className="wc-td">
                            <span
                              className={`wc-status ${
                                org.status === "Active" ? "text-success" : "text-danger"
                              } fw-semibold`}
                            >
                              {org.status}
                            </span>
                          </td>
                          <td className="wc-td text-center">
                            <Button
                              size="sm"
                              color="link"
                              className="wc-action-btn wc-btn-history"
                              onClick={() => openTransactionModal(org)}
                              title="View Transactions"
                            >
                              <AiOutlineHistory size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ────── TRANSACTION HISTORY MODAL ────── */}
      <Modal isOpen={transactionModal} toggle={closeModals} centered size="lg" className="wc-modal">
        <ModalHeader toggle={closeModals} className="wc-modal-header">
          Transaction History — {selectedOrg?.name}
        </ModalHeader>
        <ModalBody>
          <table responsive className="wc-transaction-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th className="text-end">Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {transactionHistory.map((tx, i) => (
                <tr key={i}>
                  <td>{new Date(tx.date).toLocaleDateString()}</td>
                  <td>{tx.type}</td>
                  <td className="text-end fw-semibold">
                    <span className={tx.amount > 0 ? "text-success" : "text-danger"}>
                      {tx.amount > 0 ? "+" : ""}₹{Math.abs(tx.amount)}
                    </span>
                  </td>
                  <td>
                    <Badge color={tx.status === "Success" ? "success" : "warning"}>
                      {tx.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeModals}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* ────── EDIT CREDIT COSTS MODAL (Super Admin only) ────── */}
      <Modal isOpen={editCostModal} toggle={closeModals} centered className="wc-modal">
        <ModalHeader toggle={closeModals} className="wc-modal-header">
          Edit Credit Costs
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md="12">
              <FormGroup>
                <label className="wc-form-label">Basic Credit Cost (₹)</label>
                <Input
                  type="number"
                  name="basicCreditCost"
                  value={formData.basicCreditCost}
                  onChange={handleInputChange}
                  min="1"
                  className="wc-input"
                />
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup>
                <label className="wc-form-label">Advanced Credit Cost (₹)</label>
                <Input
                  type="number"
                  name="advancedCreditCost"
                  value={formData.advancedCreditCost}
                  onChange={handleInputChange}
                  min="1"
                  className="wc-input"
                />
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup>
                <label className="wc-form-label">Combo Credit Cost (₹)</label>
                <Input
                  type="number"
                  name="comboCreditCost"
                  value={formData.comboCreditCost}
                  onChange={handleInputChange}
                  min="1"
                  className="wc-input"
                />
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup>
                <label className="wc-form-label">PAN Verification Cost (₹)</label>
                <Input
                  type="number"
                  name="panVerificationCost"
                  value={formData.panVerificationCost}
                  onChange={handleInputChange}
                  min="1"
                  className="wc-input"
                />
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup>
                <label className="wc-form-label">Court Case Check Cost (₹)</label>
                <Input
                  type="number"
                  name="courtCaseCheckCost"
                  value={formData.courtCaseCheckCost}
                  onChange={handleInputChange}
                  min="1"
                  className="wc-input"
                />
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup>
                <label className="wc-form-label">PAN To Bank Account Cost (₹)</label>
                <Input
                  type="number"
                  name="panToBankAccountCost"
                  value={formData.panToBankAccountCost}
                  onChange={handleInputChange}
                  min="1"
                  className="wc-input"
                />
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup>
                <label className="wc-form-label">Mobile To Bank Account Cost (₹)</label>
                <Input
                  type="number"
                  name="mobileToBankAccountCost"
                  value={formData.mobileToBankAccountCost}
                  onChange={handleInputChange}
                  min="1"
                  className="wc-input"
                />
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup>
                <label className="wc-form-label">Mobile Prefill Cost (₹)</label>
                <Input
                  type="number"
                  name="mobilePrefillCost"
                  value={formData.mobilePrefillCost}
                  onChange={handleInputChange}
                  min="0"
                  className="wc-input"
                />
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup>
                <label className="wc-form-label">PAN To Bank Account Provider</label>
                <Input
                  type="select"
                  name="panToBankAccountProvider"
                  value={formData.panToBankAccountProvider}
                  onChange={handleInputChange}
                  className="wc-input"
                >
                  <option value="idspay">IDS Pay</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md="12">
              <FormGroup>
                <label className="wc-form-label">Mobile To Bank Account Provider</label>
                <Input
                  type="select"
                  name="mobileToBankAccountProvider"
                  value={formData.mobileToBankAccountProvider}
                  onChange={handleInputChange}
                  className="wc-input"
                >
                  <option value="idspay">IDS Pay</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSaveCosts}>
            Save Changes
          </Button>
          <Button color="secondary" onClick={closeModals}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}